import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, Send, Smile } from 'lucide-react';
import { useApp } from '../context/AppContext';

const ChatSidebar: React.FC = () => {
  const { state, dispatch } = useApp();
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  console.log('💬 ChatSidebar: Component rendered, isMultiplayerMode:', state.isMultiplayerMode);
  console.log('💬 ChatSidebar: Current session:', state.currentSession?.id);
  console.log('💬 ChatSidebar: Chat messages count:', state.chatMessages?.length || 0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [state.chatMessages]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showEmojiPicker) {
        const target = event.target as HTMLElement;
        if (!target.closest('.emoji-picker-container')) {
          setShowEmojiPicker(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const sendMessage = async () => {
    if (!message.trim()) return;

    console.log('💬 ChatSidebar: Sending message:', message.trim());
    console.log('💬 ChatSidebar: Current session:', state.currentSession?.id);
    console.log('💬 ChatSidebar: Current user:', state.currentUser.id);

    const messageContent = message.trim();
    
    // Add to local state first for immediate feedback
    const chatMessage = {
      id: Date.now().toString(),
      userId: state.currentUser.id,
      userName: state.currentUser.name,
      userColor: state.currentUser.color,
      userAvatar: state.currentUser.avatar,
      content: messageContent,
      messageType: 'text' as const,
      timestamp: new Date(),
    };
    
    dispatch({ type: 'ADD_CHAT_MESSAGE', payload: chatMessage });
    setMessage(''); // Clear input after adding to local state
    
    // Save to Firestore if in a session
    if (state.currentSession && state.currentUser.id) {
      try {
        console.log('💬 ChatSidebar: Saving to Firestore...');
        const { chatService } = await import('../services/chatService');
        await chatService.sendMessage(
          state.currentSession.id,
          state.currentUser.id,
          state.currentUser.name,
          state.currentUser.color,
          state.currentUser.avatar,
          messageContent,
          'text'
        );
        console.log('💬 ChatSidebar: Message saved to Firestore successfully');
        // The Firebase listener will handle updating the local state
      } catch (error) {
        console.error('Failed to save chat message to Firestore:', error);
        // Message is already in local state, so no need to add again
      }
    } else {
      console.log('💬 ChatSidebar: Not in session, message added to local state only');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const addEmoji = (emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  // Common emojis for quick access
  const commonEmojis = ['😀', '😂', '😊', '😍', '🤔', '👍', '👎', '❤️', '🎉', '🔥', '💯', '👏', '🙌', '😎', '🤯', '😱'];

  const formatTime = (timestamp: Date | any) => {
    try {
      // Handle both Date objects and Firestore Timestamps
      const date = timestamp instanceof Date ? timestamp : (timestamp as any).toDate();
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Invalid time';
    }
  };

  return (
    <div className="bg-dark-800 rounded-xl p-4 h-72 flex flex-col shadow-lg border border-dark-700">
      {/* Header */}
      <div className="flex items-center space-x-2 mb-3 flex-shrink-0">
        <MessageCircle className="h-5 w-5 text-success-500" />
        <h3 className="text-lg font-semibold text-white">Chat</h3>
        <span className="text-sm text-dark-400">({state.chatMessages.length})</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-2 mb-3 scrollbar-hide">
        {state.chatMessages.length === 0 ? (
          <div className="text-center text-dark-500 py-8">
            <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No messages yet</p>
            <p className="text-xs">Start a conversation!</p>
          </div>
        ) : (
          (state.chatMessages || []).map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.userId === state.currentUser.id ? 'justify-end' : 'justify-start'} animate-fade-in`}
            >
              <div
                className={`max-w-xs px-3 py-2 rounded-2xl shadow-sm ${
                  msg.userId === state.currentUser.id
                    ? 'bg-accent-600 text-white rounded-br-md'
                    : 'bg-dark-700 text-dark-200 border border-dark-600 rounded-bl-md'
                }`}
              >
                {/* Message Header */}
                <div className="flex items-center space-x-2 mb-1">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center text-xs font-semibold border border-dark-600"
                    style={{ backgroundColor: msg.userColor }}
                  >
                    {msg.userName.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs font-medium opacity-90">
                    {msg.userName}
                  </span>
                  <span className="text-xs opacity-60">
                    {formatTime(msg.timestamp)}
                  </span>
                </div>
                
                {/* Message Content */}
                <div className="text-sm leading-relaxed">
                  {msg.content}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="flex space-x-2 flex-shrink-0">
        <div className="flex-1 relative">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="w-full bg-dark-700 text-white rounded-full px-4 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-success-500 border border-dark-600 transition-all duration-200"
          />
          <button 
            onClick={toggleEmojiPicker}
            className={`absolute right-2 top-1/2 transform -translate-y-1/2 transition-colors ${
              showEmojiPicker ? 'text-accent-500' : 'text-dark-400 hover:text-white'
            }`}
          >
            <Smile className="h-4 w-4" />
          </button>
          
          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="emoji-picker-container absolute bottom-full right-0 mb-2 bg-dark-800 border border-dark-600 rounded-lg p-3 shadow-xl z-10">
              <div className="grid grid-cols-4 gap-2">
                {commonEmojis.map((emoji, index) => (
                  <button
                    key={index}
                    onClick={() => addEmoji(emoji)}
                    className="p-2 hover:bg-dark-700 rounded-lg transition-colors text-lg"
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          onClick={sendMessage}
          disabled={!message.trim()}
          className="p-2 bg-success-600 hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-full transition-all duration-200 shadow-md"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ChatSidebar;
